import { get } from 'lodash';
import { setTags } from 'store/pageMeta/slice';
import { getNmpMetadata } from 'config/nmpMetadata';

// Converts Gateway SEO Response into page meta tags on both server and client
export default (store) =>
	(next) =>
	(action = {}) => {
		const meta =
			get(action, 'payload.meta') || get(action, 'payload.result.meta');
		const seo =
			get(action, 'payload.seo') || get(action, 'payload.result.seo');
		const location =
			get(action, 'payload.location') ||
			get(action, 'payload.result.location');
		const isFiltered = get(meta, 'filteredPage', false);

		// Process metadata on both server and client to support dynamic updates
		const { app, pageMeta: currentPageMeta } = store.getState();
		const existingTags = currentPageMeta.tags || {};

		if (action.payload && (meta || seo)) {
			// When the page is filtered, strip any carried-forward canonical link
			// from existing state — otherwise the canonical from the unfiltered
			// page load persists even though we skip adding a new one. (SF-19077)
			const existingLinks = [...(existingTags.links || [])];
			const links = isFiltered
				? existingLinks.filter((link) => link.rel !== 'canonical')
				: existingLinks;

			const pageMeta = {
				title: existingTags.title || null,
				meta: { ...(existingTags.meta || {}) },
				og: { ...(existingTags.og || {}) },
				twitter: { ...(existingTags.twitter || {}) },
				links,
				structuredData: existingTags.structuredData || null,
				filteredPage: isFiltered,
			};

			const title = get(meta, 'title');
			const description = get(meta, 'description');
			const structuredData = get(meta, 'structuredData');
			// Use canonicalWithQueryParams to preserve pagination parameters
			const canonicalWithQueryParams = isFiltered
				? null
				: get(seo, 'canonicalWithQueryParams');
			const canonical = isFiltered ? null : get(seo, 'canonical');

			const prevPageUrl = get(seo, 'prev');
			const nextPageUrl = get(seo, 'next');
			const noindex = get(meta, 'noindex', false);

			if (title) {
				pageMeta.title = title;
				pageMeta.og.title = title;
				pageMeta.twitter.title = title;
			}

			if (description) {
				pageMeta.meta.description = description;
				pageMeta.og.description = description;
				pageMeta.twitter.description = description;
			}

			// NMP fallback: apply template when API returned no title/description
			const nmpKeyword = get(action, 'meta.nmpKeyword');
			if (nmpKeyword) {
				const nmpMeta = getNmpMetadata(nmpKeyword);
				// TODO :: the commented below conditions have to be uncommented when pita/cms (gateway) return correct meta details for nmp pages
				// if (!title && nmpMeta.title) {
				pageMeta.title = nmpMeta.title;
				pageMeta.og.title = nmpMeta.title;
				pageMeta.twitter.title = nmpMeta.title;
				// }
				// if (!description && nmpMeta.description) {
				pageMeta.meta.description = nmpMeta.description;
				pageMeta.og.description = nmpMeta.description;
				pageMeta.twitter.description = nmpMeta.description;
				// }
			}

			if (structuredData) {
				pageMeta.structuredData = structuredData;

				if (structuredData.description) {
					pageMeta.structuredData = {
						...pageMeta.structuredData,
						description: encodeURIComponent(
							structuredData.description || ''
						),
					};
				}
			}

			// Use canonicalWithQueryParams if available (for paginated pages), otherwise use canonical
			let canonicalUrl = canonicalWithQueryParams || canonical;

			// NMP pages: construct canonical from the route slug unless filtered.
			// Filtered NMP pages should not have a canonical — the unfiltered
			// slug page is the canonical version (e.g. /near-me/wine-storage.html).
			const nmpSlug = get(action, 'meta.nmpSlug');
			if (nmpSlug && !isFiltered) {
				const basePath = `/near-me/${nmpSlug}.html`;
				const pageParam =
					get(action, 'payload.seo.canonicalWithQueryParams') ||
					get(action, 'payload.result.seo.canonicalWithQueryParams');
				const pageMatch =
					pageParam && pageParam.match(/[?&](page=\d+)/);
				canonicalUrl = pageMatch
					? `${basePath}?${pageMatch[1]}`
					: basePath;
			}

			if (canonicalUrl) {
				const normalizedUrl = canonicalUrl.startsWith('/')
					? canonicalUrl
					: `/${canonicalUrl}`;
				const fullUrl = normalizedUrl.startsWith('http')
					? normalizedUrl
					: `${app.baseUrl}${normalizedUrl}`;

				pageMeta.og.url = fullUrl;
				const canonicalIndex = pageMeta.links.findIndex(
					(link) => link.rel === 'canonical'
				);
				const canonicalLink = {
					rel: 'canonical',
					href: fullUrl,
				};
				if (canonicalIndex >= 0) {
					pageMeta.links[canonicalIndex] = canonicalLink;
				} else {
					pageMeta.links.push(canonicalLink);
				}
			}

			pageMeta.links = pageMeta.links.filter(
				(link) => link.rel !== 'prev' && link.rel !== 'next'
			);

			if (prevPageUrl) {
				const normalizedPrev = prevPageUrl.startsWith('/')
					? prevPageUrl
					: `/${prevPageUrl}`;
				const fullPrev = normalizedPrev.startsWith('http')
					? normalizedPrev
					: `${app.baseUrl}${normalizedPrev}`;
				pageMeta.links.push({ rel: 'prev', href: fullPrev });
			}
			if (nextPageUrl) {
				const normalizedNext = nextPageUrl.startsWith('/')
					? nextPageUrl
					: `/${nextPageUrl}`;
				const fullNext = normalizedNext.startsWith('http')
					? normalizedNext
					: `${app.baseUrl}${normalizedNext}`;
				pageMeta.links.push({ rel: 'next', href: fullNext });
			}

			// Set Open Graph defaults
			if (!pageMeta.og.type) {
				pageMeta.og.type = 'website';
			}
			// Set default og:image if not already set (matches hardcoded value in PageMetadata component)
			if (!pageMeta.og.image) {
				pageMeta.og.image = `${app.baseUrl}/static/images/logos/spareFoot-social-logo.webp`;
			}

			// Set Twitter Card defaults
			if (!pageMeta.twitter.card) {
				pageMeta.twitter.card = 'summary_large_image';
			}
			if (!pageMeta.twitter.site) {
				pageMeta.twitter.site = '@sparefoot';
			}
			// Set twitter:image to match og:image
			if (!pageMeta.twitter.image) {
				pageMeta.twitter.image = pageMeta.og.image;
			}

			store.dispatch(setTags(pageMeta, noindex));
		}

		return next(action);
	};
